import { useCallback, useEffect, useMemo, useState } from "react";
import { BUYER, ESCALATION_REASONS, SELLER } from "./constants";
import styles from "./styles.module.scss";
import TextArea from "../../../components/forms/textarea/TextArea";
import { WRITE_REASONS } from "./constants";
import { AlertColor, Checkbox, FormControlLabel } from "@mui/material";
import PrimaryButton, {
  PrimaryButtonType,
} from "../../../components/common/PrimaryButton/PrimaryButton";
import { ESCALATE_TO_WAFFY } from "../constants";
import { EContractActions } from "../../../setup/api/services/contractActions/enum";
import { IContractActionRequestBody } from "../../../setup/api/services/contractActions/models";
import { useParams } from "react-router-dom";
import { ESenderRole } from "../../../setup/api/services/contract/models";
import { doAction } from "../../../setup/api/services/contractActions";
import React from "react";
import CustomizedSnackbars from "../../../components/customizedSnackbars";
import { AuthConfig } from "../../../setup/context/config/authConfig";

interface IProps {
  isWaitingDelivery?: boolean;
  isWaitingInspection?: boolean;
  sellerId: string;
  buyerId: string | undefined;
}
const CHARACTER_LIMIT = 200;

const EscalationForm = ({
  isWaitingDelivery,
  isWaitingInspection,
  sellerId,
  buyerId,
}: IProps) => {
  const [type, setType] = useState("");
  const [additionalReasons, setAdditionalReasons] = useState("");
  const [checked, setChecked] = useState<string[]>([]);
  const { id } = useParams<{ id: string }>();
  const isSeller = useMemo(() => type === "seller", [type]);
  const isBuyer = useMemo(() => type === "buyer", [type]);

  const adminId = AuthConfig.getUserInfo().id;

  const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false);
  const [snackBarColor, setSnackBarColor] = useState<AlertColor>();
  const [snackBarTitle, setSnackBarTitle] = useState<string>("");

  const handleSeller = useCallback(() => setType("seller"), []);
  const handleBuyer = useCallback(() => setType("buyer"), []);

  const toggleCheck = useCallback(
    (key: string, isChecked: boolean) => {
      if (isChecked === true) setChecked([...checked, key]);
      else
        setChecked(
          checked.filter((item) => {
            return item !== key;
          })
        );
    },
    [checked]
  );

  const refreshContractDetails = useCallback(() => {
    window.location.reload();
  }, []);

  const renderSnackBar = useCallback(() => {
    return snackBarOpen ? (
      <CustomizedSnackbars
        open={snackBarOpen}
        title={snackBarTitle}
        type={snackBarColor}
      />
    ) : null;
  }, [snackBarColor, snackBarOpen, snackBarTitle]);

  useEffect(() => {
    if (snackBarOpen) {
      setTimeout(() => {
        setSnackBarTitle("");
        setSnackBarOpen(false);
      }, 5000);
    }
  }, [snackBarOpen]);

  const onClick = useCallback(async () => {
    const reasons = isSeller ? [""] : [...checked];
    const userId = isSeller ? sellerId : buyerId || "";
    const escalationDto = {
      ticketId: "",
      userId,
      reasons,
      message: additionalReasons,
    };
    let action: EContractActions | undefined = undefined;
    if (isWaitingDelivery) {
      if (isBuyer) {
        action = EContractActions.ADMIN_ESCALATE_DELIVERY_FOR_CUSTOMER;
      } else {
        action = EContractActions.ADMIN_ESCALATE_DELIVERY_FOR_PROVIDER;
      }
    } else if (isWaitingInspection) {
      if (isBuyer) {
        action = EContractActions.ADMIN_ESCALATE_ITEM_FOR_CUSTOMER;
      } else {
        action = EContractActions.ADMIN_ESCALATE_ITEM_FOR_PROVIDER;
      }
    }

    if (!action) return;
    const body: IContractActionRequestBody = {
      contractId: id || "",
      userId: adminId,
      actorRole: ESenderRole.ADMIN,
      receiverId: sellerId,
      senderId: buyerId || "",
      contractAction: action,
      escalationDTO: escalationDto,
    };

    try {
      const res = await doAction(body);
      setSnackBarTitle(JSON.stringify(res.sent));
      setSnackBarColor("success");
      setSnackBarOpen(true);
      refreshContractDetails();
    } catch (error) {
      setSnackBarTitle(JSON.stringify(error));
      setSnackBarColor("error");
      setSnackBarOpen(true);
      console.error(error);
    }
  }, [
    additionalReasons,
    adminId,
    buyerId,
    checked,
    id,
    isBuyer,
    isSeller,
    isWaitingDelivery,
    isWaitingInspection,
    refreshContractDetails,
    sellerId,
  ]);

  return (
    <div className={styles.container}>
      {renderSnackBar()}
      <div className={styles.btnContainer}>
        <PrimaryButton
          buttonType={
            isSeller ? PrimaryButtonType.DEFAULT : PrimaryButtonType.DISABLED
          }
          onClick={handleSeller}>
          {SELLER}
        </PrimaryButton>
        <PrimaryButton
          buttonType={
            isBuyer ? PrimaryButtonType.DEFAULT : PrimaryButtonType.DISABLED
          }
          onClick={handleBuyer}>
          {BUYER}
        </PrimaryButton>
      </div>

      {(isWaitingDelivery || isWaitingInspection) && type ? (
        <>
          {isBuyer
            ? ESCALATION_REASONS.map((reason, index) => (
                <div key={reason.key || index}>
                  <FormControlLabel
                    className={styles.checkBoxLabel}
                    control={
                      <Checkbox
                        key={reason.key}
                        checked={checked.includes(reason.key)}
                        onChange={(event) => {
                          toggleCheck(reason.key, event.target.checked);
                        }}
                      />
                    }
                    label={reason.value}
                  />
                </div>
              ))
            : null}

          <TextArea
            label={WRITE_REASONS}
            maxCharacters={CHARACTER_LIMIT}
            value={additionalReasons}
            handleChange={(event) => {
              setAdditionalReasons(event.target.value);
            }}
          />
        </>
      ) : null}

      {type ? (
        <PrimaryButton
          id={`contract_details_escalate_confirm`}
          buttonType={PrimaryButtonType.DANGER}
          onClick={onClick}
          disabled={
            (isBuyer && checked.length === 0) || additionalReasons.length === 0
          }>
          {ESCALATE_TO_WAFFY}
        </PrimaryButton>
      ) : null}
    </div>
  );
};

export default EscalationForm;
